// An import while its check or Apply runs, once one stopped, once applied and once discarded (UC-IMPORTS-003 step 2, E2;
// UC-IMPORTS-004 steps 6–10, A8, A10, E7–E9; UC-IMPORTS-005 step 3, A2, A3; UC-IMPORTS-006 A1): the lines done of the lines to do,
// read again by itself, then "Making the list current"; a stopped check's Check again and a stopped run's Resume, each saying
// nothing was changed; the result with the counts and the list now current; a discarded import read-only; and on the last three
// the original file and the error report through their signed links, and the lines with what each became.
import type { ImportJobView, ImportRowOutcome, ImportRowVerdict, ImportRowView } from "@visionx/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { importJobs } from "../api/endpoints.imports";
import { pricing } from "../api/endpoints.pricing";
import { describeError } from "../api/problem";
import { startDownload } from "../lib/download";
import { DOT, formatCount, formatDate, formatMoney, formatTime, localDate, percentFromBp } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Chip, Empty, ErrorAlert, KV, Loading, Pager, Panel } from "../ui/bits";
import { VerdictChip, errorWords, failureWords, outcomeWords, scopeWords, useImportAccess } from "./ImportsShared";
import { lensRangesWords } from "./PriceImportReview";
import { dayText, shortName, todayLocal } from "./PricingShared";

/**
 * What "Recalculate selling prices from markup" does, as the run says it while it applies and once applied (UC-IMPORTS-004 A1
 * step 4; BR-PRICING-022): a price set by hand is raised to the list's, never lowered — as the Review says of a line it keeps.
 * An import whose Apply stopped, or that was discarded, raised nothing: its Applied line names the choice (the verification of
 * round 2, 25 Oct 2026, VP-1) — and so does one Imported with nothing to change, which made no list current and set no price
 * (UC-IMPORTS-004 A8; the verification of round 4, 25 Oct 2026, V4S-1): the deed is said only of an import that made its list
 * current.
 */
const RECALCULATE_RUNNING = "raising prices set by hand to the list's, never lowering them";
const RECALCULATE_DONE = "prices set by hand raised to the list's, never lowered";
const RECALCULATE_ASKED = "Recalculate ticked: raises prices set by hand to the list's, never lowers them";

/**
 * UC-IMPORTS-003 step 2, UC-IMPORTS-004 step 6: the lines done of the lines to do, then the final step — at a practice of one store
 * said with no word of branches (UC-IMPORTS-005 A8).
 */
export function RunProgress({ job }: { job: ImportJobView }) {
  const oneStore = useOneStore();
  const p = job.progress;
  const checking = job.status === "Checking";
  const total = p?.total ?? job.lines;
  const done = Math.min(p?.done ?? 0, total);
  const finalStep = !checking && total > 0 && done >= total;
  const title = checking ? `Checking the file — ${formatCount(done)} of ${formatCount(total)} lines`
    : finalStep ? "Making the list current" : `Writing the new list — ${formatCount(done)} of ${formatCount(total)} lines`;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <Panel title={checking ? "Checking" : "Applying"} meta={checking ? "the dry run — it writes only this import's own lines" : `${job.commit?.mode === "AllOrNothing" ? "all or nothing" : "valid lines only"}${job.commit?.recalculateSellingPrices ? ` · ${RECALCULATE_RUNNING}` : ""}`}>
      <div className="stack" style={{ gap: 10 }} aria-live="polite">
        <div className="strong">{title}</div>
        <div className="progress" role="progressbar" aria-label={checking ? "Check" : "Apply"} aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}><i style={{ width: `${finalStep ? 100 : pct}%` }} /></div>
        <span className="help">{checking
          ? "Nothing outside this import changes while the file is checked. You may leave this page; the check carries on and the Review opens when it ends."
          : finalStep ? "The new list, its prices, the new lens lines and the flags take effect together in this one step."
          : `Nothing becomes current until the last step: ${oneStore ? "the current prices stay" : "every branch keeps its current prices"} until then. You may leave this page; the run carries on.`}</span>
      </div>
    </Panel>
  );
}

/** UC-IMPORTS-003 E2: the check stopped — nothing changed; Check again, or back to the mapping; Cancel import is in the head. */
export function CheckStopped({ job }: { job: ImportJobView }) {
  const qc = useQueryClient();
  const access = useImportAccess();
  const block = useWriteBlock();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const again = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try { qc.setQueryData(["import", job.id], await importJobs.check(job.id)); void qc.invalidateQueries({ queryKey: ["imports"] }); }
    // A refusal — another user checked it meanwhile, the organisation suspended — reads the import again, so the page shows its step.
    catch (err) { setError(err); void qc.invalidateQueries({ queryKey: ["import", job.id], exact: true }); }
    finally { setBusy(false); }
  };
  const suspended = job.failure?.phase === "suspended";
  return (
    <>
      <Alert tone="crit" role="alert" actions={access.write && <>
        <Link className="btn sm" to={`/price-import/${job.id}?step=mapping`}>Change the mapping</Link>
        <button className="btn sm primary" type="button" disabled={busy || block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void again()}>{busy ? "Checking…" : "Check again"}</button>
      </>}>
        <b>The check stopped — nothing was changed.</b> {failureWords(job)}{suspended ? "" : " Check the file again, or cancel the import."}
      </Alert>
      {error !== null && <ErrorAlert error={error} />}
      <ImportFacts job={job} />
    </>
  );
}

/** UC-IMPORTS-004 E7–E9, A10: the run stopped — nothing became current; Resume where it may be resumed, Cancel import in the head. */
export function ImportStopped({ job }: { job: ImportJobView }) {
  const qc = useQueryClient();
  const access = useImportAccess();
  const block = useWriteBlock();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const resume = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try { qc.setQueryData(["import", job.id], await importJobs.resume(job.id)); void qc.invalidateQueries({ queryKey: ["imports"] }); }
    catch (err) { setError(err); void qc.invalidateQueries({ queryKey: ["import", job.id], exact: true }); }
    finally { setBusy(false); }
  };
  const canResume = access.write && job.moves.includes("resume");
  return (
    <>
      <Alert tone="crit" role="alert" actions={canResume && <button className="btn sm primary" type="button" disabled={busy || block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => void resume()}>{busy ? "Resuming…" : "Resume"}</button>}>
        <b>The import stopped — nothing was changed.</b> {failureWords(job)} {canResume ? "Every list, price and flag is as it was; Resume carries on from the last finished part, or cancel the import." : "Every list, price and flag is as it was."}
      </Alert>
      {error !== null && <Alert tone="crit" role="alert"><b>Not resumed.</b> {describeError(error)}</Alert>}
      <ImportFacts job={job} />
    </>
  );
}

/** UC-IMPORTS-004 steps 9–10, A8, A9: Imported, with the counts and the list now current; the lines and the downloads below. */
export function ImportResult({ job }: { job: ImportJobView }) {
  const list = useQuery({ queryKey: ["pricing", "price-list", job.priceListId ?? ""], queryFn: () => pricing.priceList(job.priceListId!), enabled: !!job.priceListId });
  const o = job.outcome;
  // The organisation's day of the instant, never UTC's: one completed at 00:30 SAST was completed that day.
  const appliedAt = job.completedAt ?? job.appliedAt;
  const appliedDay = appliedAt ? localDate(new Date(appliedAt)) : "";
  const nothing = o !== null && o.created === 0 && o.changed === 0 && o.flagged === 0;
  const l = list.data?.data;
  const where = job.scope === "AllBranches" ? "" : ` at ${job.branch?.name ?? "the branch"}`;
  const from = appliedDay && appliedDay === todayLocal() ? "today" : dayText(appliedDay);
  return (
    <>
      <Alert tone="ok" role="status">
        <b>Imported.</b> {o ? outcomeWords(o) : ""}.{" "}
        {nothing && !job.priceListId ? "Nothing changed: the file is the one the current list came from, so the current list stays as it was." : (
          <>The {dayText(l?.listDate ?? job.listDate)} list is current{where} — {formatCount(l?.lineCount ?? job.lines)} lines; its prices apply from {from}.</>
        )}
      </Alert>
      <ImportFacts job={job} />
      <ImportLines job={job} />
    </>
  );
}

/** UC-IMPORTS-005 A3, UC-IMPORTS-006 A1 step 3: read-only — who discarded it and what that withdrew; the file and its report stay. */
export function DiscardedPanel({ job }: { job: ImportJobView }) {
  return (
    <>
      <Alert tone="info" role="status">
        <b>Discarded {formatDate(job.discardedAt)} {formatTime(job.discardedAt)}{job.discardedBy ? ` by ${job.discardedBy.displayName}` : ""}.</b>{" "}
        Its draft list was withdrawn and the lens lines it added were voided; nothing became current. The original file and its error report are kept.
      </Alert>
      <ImportFacts job={job} />
      <ImportLines job={job} />
    </>
  );
}

/** The import's terms and when and by whom each step was taken (UC-IMPORTS-005 step 3), with its two downloads. */
function ImportFacts({ job }: { job: ImportJobView }) {
  const oneStore = useOneStore();
  const scope = scopeWords(job, oneStore);
  const who = (u: ImportJobView["uploadedBy"]) => (u ? `${shortName(u.displayName)} · ${String(u.controlId).padStart(4, "0")}` : "—");
  const at = (iso: string | null) => (iso ? `${formatDate(iso)} ${formatTime(iso)}` : "—");
  const rows: [ReactNode, ReactNode][] = [
    ["Supplier", job.supplier?.name ?? "—"],
    ["List", <>{job.listName}{DOT}{job.listDate ? `dated ${dayText(job.listDate)}` : "no date"}{scope !== null ? <>{DOT}{scope}</> : null}{job.lensMarkupBp !== null ? <>{DOT}+{percentFromBp(job.lensMarkupBp)} on lab price</> : null}</>],
    ["File", <><span className="mono">{job.file.name}</span>{DOT}{formatCount(job.lines)} price lines</>],
    ["Uploaded", <>{at(job.uploadedAt)}{DOT}{who(job.uploadedBy)}{job.uploadBranch ? <>{DOT}{job.uploadBranch.name}</> : null}</>],
    ...(job.checkedAt ? [["Checked", at(job.checkedAt)] as [ReactNode, ReactNode]] : []),
    ...(job.appliedAt ? [["Applied", <>{at(job.appliedAt)}{DOT}{who(job.appliedBy)}{job.commit ? <>{DOT}{job.commit.mode === "AllOrNothing" ? "all or nothing" : "valid lines only"}{job.commit.recalculateSellingPrices ? ` · ${job.status === "Imported" && job.priceListId !== null ? RECALCULATE_DONE : RECALCULATE_ASKED}` : ""}</> : null}</>] as [ReactNode, ReactNode]] : []),
    ...(job.completedAt ? [["Completed", at(job.completedAt)] as [ReactNode, ReactNode]] : []),
    ...(job.discardedAt ? [["Discarded", <>{at(job.discardedAt)}{DOT}{who(job.discardedBy)}</>] as [ReactNode, ReactNode]] : []),
  ];
  return (
    <Panel title="This import" meta={job.counts ? `${formatCount(job.counts.parsed)} parsed · ${formatCount(job.counts.changed)} changed · ${formatCount(job.counts.new)} new · ${formatCount(job.counts.unchanged)} unchanged · ${formatCount(job.counts.discontinued)} discontinued · ${formatCount(job.counts.errors)} rejected` : undefined}
      foot={<Downloads job={job} />}>
      <KV rows={rows} />
    </Panel>
  );
}

/** UC-IMPORTS-005 steps 4–5, A2: each download follows the route's signed link from the page; following it is audited. */
export function Downloads({ job }: { job: ImportJobView }) {
  return (
    <div className="row c gap-8">
      <button className="btn sm" type="button" onClick={() => startDownload(importJobs.filePath(job.id), job.file.name)}>Download original file</button>
      {job.checkedAt !== null || job.counts !== null
        ? <button className="btn sm" type="button" onClick={() => startDownload(importJobs.errorReportPath(job.id), `${job.file.name.replace(/\.csv$/i, "")} - errors.csv`)}>Download error report</button>
        : null}
      <span className="help">Each link lasts 15 minutes; a download is recorded in the audit log.</span>
    </div>
  );
}

const OUTCOME_TONE: Readonly<Record<ImportRowOutcome, "ok" | "info" | "neutral" | "warn" | "crit">> = { Created: "ok", Changed: "info", Unchanged: "neutral", Skipped: "neutral", Errored: "crit", Flagged: "warn" };

/**
 * What became of a discontinued line's lens (BR-IMPORTS-010, BR-CATALOGUE-020; UC-IMPORTS-004 step 10): the line still on
 * another list of the supplier where one carried its code — only a line the import did not flag, a flagged line's code having
 * been on no other list, whatever its view says (the review of the fixes of 24 Oct 2026) — and of a flagged line, its lens hidden
 * from new orders where this import's list hid it, inactive where it was already, kept on offer where another current list
 * names it. The list is named with its date, and "since replaced" once a later list of its scope has superseded it, so a list
 * replaced since is not read as the current one of the same name (Proposed, the verification of round 2, 25 Oct 2026, RS-2).
 */
function BecameNote({ job, r }: { job: ImportJobView; r: ImportRowView }) {
  const kept = r.keptBy;
  const list = kept ? `${kept.name} of ${dayText(kept.listDate)}` : "";
  const words = r.outcome !== "Flagged" ? (kept?.reason === "carried" ? `still on ${list}${kept.current ? "" : ", since replaced"}` : null)
    : !r.product ? null
    : r.product.status === "Active" ? (kept?.reason === "named" ? `kept on offer — ${list} ${kept.current ? "names it" : "named it, since replaced"}` : null)
    : r.product.hiddenByPriceList?.importJobId === job.id ? "hidden from new orders" : "inactive";
  return words ? <div className="small faint">{words}</div> : null;
}
const LINE_CHIPS: readonly [string, ImportRowVerdict | "all"][] = [["All lines", "all"], ["Changed", "Changed"], ["New", "New"], ["Errors", "Error"], ["Discontinued", "Discontinued"], ["Unchanged", "Unchanged"]];
const PAGE = 50;

/** UC-IMPORTS-005 step 3: the working list read-only — each line's status, prices and what it became (its product, its list line, and once applied what its lens took of its power ranges). */
function ImportLines({ job }: { job: ImportJobView }) {
  const [verdict, setVerdict] = useState<ImportRowVerdict | "all">("all");
  const [cursors, setCursors] = useState<string[]>([]);
  useEffect(() => { setCursors([]); }, [verdict]);
  const cursor = cursors.at(-1);
  const rows = useQuery({
    queryKey: ["import", job.id, "rows", "read", job.status, verdict, cursor ?? ""],
    queryFn: () => importJobs.rows(job.id, verdict === "all" ? { } : { verdict: [verdict] }, { limit: PAGE, cursor, count: true }), placeholderData: keepPreviousData,
  });
  if (!job.counts) return null;
  const items = rows.data?.data.items ?? [];
  const total = rows.data?.data.totalCount;
  const first = cursors.length * PAGE + 1;
  return (
    <Panel title="Lines" meta="each line of the file and of the list it was checked against, with what it became" tight
      head={<div className="chips right" role="group" aria-label="Lines">{LINE_CHIPS.map(([label, v]) => (
        <button key={v} type="button" className={`chip ${verdict === v ? "accent" : "neutral off"}`} aria-pressed={verdict === v} onClick={() => setVerdict(v)}>{label}</button>
      ))}</div>}
      foot={(cursors.length > 0 || rows.data?.data.nextCursor) ? <Pager label={`${formatCount(first)}–${formatCount(first + items.length - 1)}${total !== undefined ? ` of ${formatCount(total)}` : ""}`} hasPrev={cursors.length > 0} hasNext={!!rows.data?.data.nextCursor}
        onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => { const n = rows.data?.data.nextCursor; if (n) setCursors((c) => [...c, n]); }} /> : undefined}>
      {rows.isPending && <div className="panel-body"><Loading what="the lines" /></div>}
      {rows.isError && <div className="panel-body"><ErrorAlert error={rows.error} /></div>}
      {rows.data && (items.length === 0 ? <Empty title="No line of this kind." /> : (
        <div className="tbl-scroll">
          <table className="lines">
            <thead><tr><th className="r wide-only">Row</th><th>Code</th><th>Description</th><th className="r">Lab price ex VAT</th><th className="r">Sell incl VAT</th><th>Status</th><th>Became</th></tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="r num faint wide-only">{r.rowNo ?? "—"}</td>
                  <td className="mono nw">{r.code ?? <span className="faint">no code</span>}{r.rowNo !== null && <div className="small faint narrow-only">row {r.rowNo}</div>}</td>
                  <td className="desc">{r.description ?? <span className="faint">—</span>}{r.lensRanges && job.status === "Imported" && <div className="small faint">{lensRangesWords(r.lensRanges, r.ticked, true)}</div>}{r.errors.map((e, i) => <div key={i} className="small err-line">{errorWords(e)}</div>)}</td>
                  <td className="money">{r.labCostCents.old !== null && r.labCostCents.new !== null && r.labCostCents.old !== r.labCostCents.new ? <><span className="was">{formatMoney(r.labCostCents.old)}</span><span className="arrow">→</span><wbr /><span className="now">{formatMoney(r.labCostCents.new)}</span></> : formatMoney(r.labCostCents.new ?? r.labCostCents.old)}</td>
                  <td className="money">{r.sellCents.old !== null && r.sellCents.new !== null && r.sellCents.old !== r.sellCents.new ? <><span className="was">{formatMoney(r.sellCents.old)}</span><span className="arrow">→</span><wbr /><span className="now">{formatMoney(r.sellCents.new)}</span></> : formatMoney(r.sellCents.new ?? r.sellCents.old)}
                    {/* A price the list did not set because a person set it — at the check, or by the time the final write came (UC-IMPORTS-004 step 10, A21). */}
                    {r.sellKept === "set_by_hand" && <div className="small faint">kept — set by hand</div>}</td>
                  <td><VerdictChip verdict={r.verdict} /></td>
                  <td>{r.outcome && <Chip tone={OUTCOME_TONE[r.outcome]}>{r.outcome}</Chip>}{r.product && <div className="small"><Link className="mono" to={`/products/${r.product.id}`}>{r.product.sku}</Link></div>}{r.verdict === "Discontinued" && <BecameNote job={job} r={r} />}{!r.outcome && !r.product && <span className="faint">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Panel>
  );
}
